import { useEffect, useRef, useState, type PropsWithChildren, type PointerEvent as ReactPointerEvent } from "react";
import { cn } from "./cn";
import { useI18n } from "../i18n";

export interface BottomSheetProps extends PropsWithChildren {
  /**
   * Whether the sheet is expanded. When `false` the sheet collapses
   * to a compact handle (peek). When `true` the sheet expands to a
   * bounded height (half → full) and content becomes scrollable.
   */
  open: boolean;
  /** Optional test id override. */
  testId?: string;
  /** Optional dismiss callback wired to the drag handle. */
  onCollapse?: () => void;
}

type Snap = "half" | "full";
const DRAG_THRESHOLD = 44;

/**
 * BottomSheet — mobile Territory/context panel (WO#1 §27, Macro #31 touch pass).
 *
 * Three snap states: PEEK (collapsed handle, `open=false`), HALF and FULL
 * (`open=true`). The drag handle supports pointer drag: drag up → FULL,
 * drag down → HALF→PEEK (dismiss). A plain tap still toggles/collapses for
 * accessibility. Safe-area aware and cleared above the mobile bottom nav so
 * canonical info/actions are never hidden. Desktop uses SideDrawer (unchanged).
 */
export function BottomSheet({ open, children, onCollapse, testId }: BottomSheetProps) {
  const { t } = useI18n();
  const [snap, setSnap] = useState<Snap>("half");
  const dragRef = useRef<{ startY: number; moved: number } | null>(null);

  // Reset to HALF whenever the sheet (re)opens.
  useEffect(() => {
    if (open) setSnap("half");
  }, [open]);

  // Claudio — when there is nothing to show (collapsed), the sheet must leave
  // ZERO visible footprint (no peek handle at the bottom of the Grid). It stays
  // mounted (testid preserved) but fully hidden; it reveals only when `open`.
  const maxHeight = !open ? "0px" : snap === "full" ? "92vh" : "60vh";

  const onHandleDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    dragRef.current = { startY: e.clientY, moved: 0 };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* non-DOM env */
    }
  };
  const onHandleMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    if (!d) return;
    d.moved = e.clientY - d.startY;
  };
  const onHandleUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* nop */
    }
    if (!d) return;
    if (d.moved < -DRAG_THRESHOLD) {
      setSnap("full");
    } else if (d.moved > DRAG_THRESHOLD) {
      if (snap === "full") setSnap("half");
      else onCollapse?.();
    } else if (Math.abs(d.moved) < 6) {
      // Treat as a tap → collapse (or step down from full).
      if (snap === "full") setSnap("half");
      else onCollapse?.();
    }
  };

  return (
    <div
      data-testid={testId ?? "bottom-sheet"}
      role="dialog"
      aria-label={t("grid.bottomSheet.aria")}
      aria-expanded={open}
      data-snap={open ? snap : "peek"}
      className={cn(
        "absolute left-0 right-0 bottom-0 z-panel",
        "flex flex-col",
        "rounded-t-xl border-t border-border-soft",
        "bg-panel/95 backdrop-blur",
        "shadow-elevated",
        "transition-[max-height] duration-med ease-out",
        "overflow-hidden",
        !open && "opacity-0 pointer-events-none invisible border-transparent shadow-none",
      )}
      style={{ maxHeight }}
    >
      <button
        type="button"
        data-testid="bottom-sheet-handle"
        onPointerDown={onHandleDown}
        onPointerMove={onHandleMove}
        onPointerUp={onHandleUp}
        aria-label={open ? t("grid.bottomSheet.dragOpen") : t("grid.bottomSheet.handle")}
        className={cn(
          "shrink-0 h-9 pt-3 pb-2 flex items-center justify-center touch-none cursor-grab active:cursor-grabbing",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--focus-ring)]",
        )}
      >
        <span aria-hidden className="block h-1 w-10 rounded-pill bg-border-strong" />
      </button>
      <div
        data-testid="bottom-sheet-body"
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overscroll-contain",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
          "transition-opacity duration-fast",
        )}
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {children}
      </div>
    </div>
  );
}
